import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';

export default function Contact() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      alert('Por favor completa todos los campos obligatorios');
      return;
    }

    if (formData.message.length > 500) {
      alert('El mensaje no puede exceder 500 caracteres');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject || 'Consulta general',
          message: formData.message
        })
      });

      const data = await response.json();

      if (response.ok) {
        alert('¡Mensaje enviado exitosamente! Te responderemos pronto.');
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        alert(data.message || 'Error al enviar el mensaje. Por favor inténtalo de nuevo.');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al enviar el mensaje. Por favor inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-lp-bg">
      <Navbar />
      
      <div className="pt-20 px-4 pb-8">
        {/* Header */}
        <div className="mb-6">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sepia-600 hover:text-sepia-800 font-ui text-sm mb-4 transition-colors duration-200"
          >
            <i className="ri-arrow-left-line"></i>
            Volver al inicio
          </button>
          
          <div className="vintage-paper aged-paper p-6 shadow-lg">
            <h1 className="font-headings text-2xl text-sepia-900 mb-2">CONTACTO</h1>
            <p className="text-sm text-sepia-600 font-ui">
              Estamos aquí para ayudarte. Envíanos tu consulta y te responderemos pronto.
            </p>
          </div>
        </div>

        {/* Contact Info */}
        <div className="vintage-paper aged-paper p-6 shadow-lg mb-6">
          <h2 className="font-headings text-lg text-sepia-900 mb-4">INFORMACIÓN DE CONTACTO</h2>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 vintage-paper-subtle rounded-full flex items-center justify-center">
                <i className="ri-mail-line text-sepia-600 text-sm"></i>
              </div>
              <div>
                <h3 className="font-ui text-base text-sepia-800 mb-1">Email</h3>
                <p className="text-sm text-sepia-700 font-body">contacto@vintageliteral.com</p>
                <p className="text-xs text-sepia-600 font-ui">Respuesta en 24-48 horas</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 vintage-paper-subtle rounded-full flex items-center justify-center">
                <i className="ri-time-line text-sepia-600 text-sm"></i>
              </div>
              <div>
                <h3 className="font-ui text-base text-sepia-800 mb-1">Horario de Atención</h3>
                <p className="text-sm text-sepia-700 font-body">Lunes a Viernes: 9:00 - 18:00</p>
                <p className="text-xs text-sepia-600 font-ui">Zona horaria: GMT-5</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 vintage-paper-subtle rounded-full flex items-center justify-center">
                <i className="ri-customer-service-line text-sepia-600 text-sm"></i>
              </div>
              <div>
                <h3 className="font-ui text-base text-sepia-800 mb-1">Soporte</h3>
                <p className="text-sm text-sepia-700 font-body">Ayuda técnica y consultas generales</p>
                <p className="text-xs text-sepia-600 font-ui">Disponible todos los días</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-6">
          <div className="vintage-paper aged-paper p-6 shadow-lg">
            <h2 className="font-headings text-lg text-sepia-900 mb-4">ENVÍANOS UN MENSAJE</h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-ui text-sepia-700 mb-2">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 text-sm font-body text-sepia-800 vintage-paper-subtle border border-sepia-300/50 rounded-[10%] focus:outline-none focus:ring-2 focus:ring-sepia-500/50"
                    placeholder="Tu nombre y apellido"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-ui text-sepia-700 mb-2">
                    Correo electrónico *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 text-sm font-body text-sepia-800 vintage-paper-subtle border border-sepia-300/50 rounded-[10%] focus:outline-none focus:ring-2 focus:ring-sepia-500/50"
                    placeholder="tu@email.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-ui text-sepia-700 mb-2">
                  Asunto
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm font-body text-sepia-800 vintage-paper-subtle border border-sepia-300/50 rounded-[10%] focus:outline-none focus:ring-2 focus:ring-sepia-500/50"
                >
                  <option value="">Selecciona un asunto</option>
                  <option value="Consulta general">Consulta general</option>
                  <option value="Problema técnico">Problema técnico</option>
                  <option value="Sugerencia">Sugerencia</option>
                  <option value="Colaboración">Colaboración</option>
                  <option value="Afiliación">Proceso de afiliación</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-ui text-sepia-700 mb-2">
                  Mensaje *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  maxLength={500}
                  className="w-full px-3 py-2 text-sm font-body text-sepia-800 vintage-paper-subtle border border-sepia-300/50 rounded-[10%] resize-none focus:outline-none focus:ring-2 focus:ring-sepia-500/50"
                  placeholder="Escribe tu mensaje aquí..."
                />
                <div className="flex justify-between items-center mt-2">
                  <p className="text-xs text-sepia-500 font-ui">
                    Describe tu consulta de manera clara y detallada
                  </p>
                  <span className="text-xs text-sepia-500 font-ui">
                    {formData.message.length}/500
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="text-center">
            <button
              type="submit"
              disabled={loading}
              className="vintage-paper-subtle text-sepia-900 hover:bg-sepia-200/70 px-6 py-3 text-sm font-ui transition-all duration-300 rounded-[15%] hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mx-auto"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Enviando...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line"></i>
                  Enviar Mensaje
                </>
              )}
            </button>
          </div>
        </form>

        {/* FAQ Section */}
        <div className="vintage-paper aged-paper p-6 shadow-lg mt-6">
          <h2 className="font-headings text-lg text-sepia-900 mb-4">PREGUNTAS FRECUENTES</h2>
          
          <div className="space-y-4">
            <div className="vintage-paper-subtle p-4 rounded-[10%]">
              <h3 className="font-ui text-base text-sepia-800 mb-2">¿Cómo puedo convertirme en usuario afiliado?</h3>
              <p className="text-sm text-sepia-700 font-body">
                Los usuarios afiliados pueden publicar contenido directamente. Contáctanos para conocer los requisitos.
              </p>
            </div>

            <div className="vintage-paper-subtle p-4 rounded-[10%]">
              <h3 className="font-ui text-base text-sepia-800 mb-2">¿Puedo colaborar con contenido?</h3>
              <p className="text-sm text-sepia-700 font-body">
                ¡Por supuesto! Aceptamos artículos sobre literatura, reseñas, ensayos y contenido relacionado.
              </p>
            </div>

            <div className="vintage-paper-subtle p-4 rounded-[10%]">
              <h3 className="font-ui text-base text-sepia-800 mb-2">¿Cómo reporto un problema técnico?</h3>
              <p className="text-sm text-sepia-700 font-body">
                Selecciona "Problema técnico" en el asunto y describe detalladamente el inconveniente.
              </p>
            </div>
          </div>
        </div>

        {/* Vintage Background */}
        <div 
          className="fixed inset-0 pointer-events-none opacity-5 z-0"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=vintage%20correspondence%20desk%2C%20antique%20writing%20materials%2C%20fountain%20pen%20and%20inkwell%2C%20old%20letters%20and%20envelopes%2C%20vintage%20postage%20stamps%2C%20warm%20sepia%20lighting%2C%20classic%20communication%20setting%2C%20aged%20paper%20stationery%2C%20brown%20and%20cream%20tones%2C%20literary%20correspondence%20atmosphere&width=375&height=812&seq=contact_bg&orientation=portrait')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mixBlendMode: 'multiply'
          }}
        />
      </div>
    </div>
  );
}